/**
 * Secure API Client for Azure Static Web Apps
 *
 * This module provides a secure way to call APIM endpoints without exposing subscription keys.
 * All sensitive operations are proxied through the backend Azure Function.
 *
 * SECURITY: Never add subscription keys to this file or reference process.env for sensitive values.
 */

import { isProduction, isDevelopment } from '../utils/config'
import { logger } from '../utils/logger'

export type SubscriptionType = 'dbo' | 'all' | 'ais' | 'dts' | 'cost' | 'saas' | 'snow' | 'snow-spm' | 'btp' | 'none'

interface SecureApiCallOptions {
  targetUrl: string
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  subscriptionType: SubscriptionType
  body?: any
  customHeaders?: Record<string, string>
  signal?: AbortSignal
}

/**
 * Get the API proxy endpoint
 * In development (localhost), use direct port 7071
 * In production/Static Web Apps, use /api/proxy
 */
function getApiProxyEndpoint(): string {
  // Check if running on localhost in development
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:7071/api/proxy'
  }
  return '/api/proxy'
}

interface ApiResponse<T = any> {
  data?: T
  error?: string
  status: number
}

/**
 * Only log in development and staging (disable in production)
 */
function devLog (message: string, data?: any): void {
  // Only disable logging when IS_PRODUCTION is true
  if (isProduction !== 'true') {
    logger.info(message, data)
  }
}

function devError (message: string, data?: any): void {
  // Only disable logging when IS_PRODUCTION is true
  if (isProduction !== 'true') {
    logger.error(message, data)
  }
}

/**
 * Parse response text as JSON, handling empty responses
 */
function parseResponseText (responseText: string): { data: any, error?: string } {
  if (responseText === '') {
    return { data: {} }
  }

  try {
    return { data: JSON.parse(responseText) }
  } catch (error_) {
    const msg = error_ instanceof Error ? error_.message : String(error_)
    return {
      data: null,
      error: `Failed to parse proxy response as JSON: ${msg}`
    }
  }
}

/**
 * Handle stringified response by parsing if needed
 */
function handleStringifiedResponse (response: any): { data: any, error?: string } {
  if (typeof response !== 'string') {
    return { data: response }
  }

  try {
    return { data: JSON.parse(response) }
  } catch (parseErr) {
    devError('❌ [secureApiClient] Failed to parse stringified response:', {
      response: response.substring(0, 500), // Log first 500 chars to avoid huge logs
      error: parseErr instanceof Error ? parseErr.message : String(parseErr)
    })
    return {
      data: null,
      error: 'Failed to parse proxy response'
    }
  }
}

/**
 * Extract actual data from proxy response envelope
 */
function extractActualData (proxyResponse: any): any {
  const hasJsonBody = typeof proxyResponse === 'object' &&
                      proxyResponse !== null &&
                      'jsonBody' in proxyResponse

  let actualData = hasJsonBody ? proxyResponse.jsonBody : proxyResponse

  // Handle case where actualData is still stringified
  if (typeof actualData === 'string') {
    try {
      actualData = JSON.parse(actualData)
    } catch (parseErr) {
      devError('❌ [secureApiCall] Failed to parse actualData:', parseErr)
      // Return the string as-is for upstream to handle
    }
  }

  return actualData
}

/**
 * Make a secure API call through the backend proxy
 *
 * @param options - The API call configuration
 * @returns Promise with the API response
 *
 * @example
 * ```typescript
 * const result = await secureApiCall({
 *   targetUrl: 'https://api.example.com/data',
 *   method: 'GET',
 *   subscriptionType: 'dbo'
 * })
 * ```
 */
export async function secureApiCall<T = any> (
  options: SecureApiCallOptions
): Promise<ApiResponse<T>> {
  try {
    // Call the backend proxy function (no subscription key needed!)
    // Use direct port 7071 in development, /api/proxy in production (Static Web Apps)
    const proxyEndpoint = getApiProxyEndpoint()
    const response = await fetch(proxyEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: options.signal,
      body: JSON.stringify({
        targetUrl: options.targetUrl,
        method: options.method,
        subscriptionType: options.subscriptionType,
        body: options.body,
        customHeaders: options.customHeaders
      })
    })

    // Read body as text first to handle empty responses gracefully
    const responseText = await response.text()

    devLog('🔵 [secureApiClient] Response received:', {
      url: options.targetUrl,
      method: options.method,
      status: response.status,
      responseText: responseText.substring(0, 500)
    })

    // Parse initial response text
    const parseResult = parseResponseText(responseText)
    if (parseResult.error) {
      devError('❌ [secureApiClient] Failed to parse response text:', parseResult.error)
      return {
        error: parseResult.error,
        status: 500
      }
    }

    let proxyResponse = parseResult.data

    // Handle stringified proxy response
    const stringifiedResult = handleStringifiedResponse(proxyResponse)
    if (stringifiedResult.error) {
      devError('❌ [secureApiClient] Failed to handle stringified response:', stringifiedResult.error)
      return {
        error: stringifiedResult.error,
        status: 500
      }
    }

    proxyResponse = stringifiedResult.data

    // Check for errors from proxy
    if (!response.ok) {
      devError('❌ [secureApiClient] Proxy returned error status:', {
        status: response.status,
        error: proxyResponse.error || 'API request failed',
        proxyResponse: JSON.stringify(proxyResponse).substring(0, 500)
      })
      return {
        error: proxyResponse.error || 'API request failed',
        status: response.status
      }
    }

    // Extract the actual data from the proxy envelope
    const actualData = extractActualData(proxyResponse)

    return {
      data: actualData,
      status: response.status
    }
  } catch (error: any) {
    devError('❌ [secureApiClient] Secure API call failed:', error)
    return {
      error: error.message || 'Network error',
      status: 500
    }
  }
}

/**
 * Helper functions for common API patterns
 */

export const secureApi = {
  /**
   * Make a GET request through the secure proxy
   */
  get: async <T = any>(
    targetUrl: string,
    subscriptionType: SubscriptionType
  ): Promise<ApiResponse<T>> => {
    return await secureApiCall<T>({
      targetUrl,
      method: 'GET',
      subscriptionType
    })
  },

  /**
   * Make a POST request through the secure proxy
   */
  post: async <T = any>(
    targetUrl: string,
    body: any,
    subscriptionType: SubscriptionType
  ): Promise<ApiResponse<T>> => {
    return await secureApiCall<T>({
      targetUrl,
      method: 'POST',
      subscriptionType,
      body
    })
  },

  /**
   * Make a PUT request through the secure proxy
   */
  put: async <T = any>(
    targetUrl: string,
    body: any,
    subscriptionType: SubscriptionType
  ): Promise<ApiResponse<T>> => {
    return await secureApiCall<T>({
      targetUrl,
      method: 'PUT',
      subscriptionType,
      body
    })
  },

  /**
   * Make a PATCH request through the secure proxy
   */
  patch: async <T = any>(
    targetUrl: string,
    body: any,
    subscriptionType: SubscriptionType
  ): Promise<ApiResponse<T>> => {
    return await secureApiCall<T>({
      targetUrl,
      method: 'PATCH',
      subscriptionType,
      body
    })
  },

  /**
   * Make a DELETE request through the secure proxy
   */
  delete: async <T = any>(
    targetUrl: string,
    subscriptionType: SubscriptionType
  ): Promise<ApiResponse<T>> => {
    return await secureApiCall<T>({
      targetUrl,
      method: 'DELETE',
      subscriptionType
    })
  }
}
